/**
 * Pure helpers for block-anchored document edit plans (CONTRACTS §6 ops): the draft response
 * schema, validation of a plan against the live blocks (with messages that say exactly what
 * is wrong, Flint page_form_fill style) before anything touches the editor, and human labels
 * for the step that edits a section.
 */

import { htmlHasPageBreak } from "../utils/pageBreakHtml";

/** Edit operations the editor bridge understands. */
export const EDIT_OPS = ["replace_block", "insert_after", "insert_before", "delete_block", "replace_text", "fill_field"];

/** Gemini responseSchema for a full draft / rewrite. */
export const DRAFT_SCHEMA = {
  type: "object",
  properties: {
    summary: { type: "string", description: "One or two sentences, addressed to the user, describing the draft." },
    html: { type: "string", description: "The complete document body as HTML." },
  },
  required: ["summary", "html"],
};

/**
 * Count non-overlapping occurrences of `needle` in `haystack`.
 * @param {string} haystack - Text to search.
 * @param {string} needle - Text to find.
 * @returns {number} Occurrences.
 */
export const countOccurrences = (haystack, needle) => {
  if (!needle) return 0;
  const text = String(haystack || "");
  let count = 0;
  let index = text.indexOf(needle);
  while (index !== -1) {
    count += 1;
    index = text.indexOf(needle, index + needle.length);
  }
  return count;
};

/**
 * Validate a planned edit against the live blocks.
 * @param {Array<object>} operations - Planned ops.
 * @param {Array<object>} blocks - Editor blocks.
 * @param {{fieldIds?: string[]}} [options] - Known field ids (fill_field is checked against them when given).
 * @returns {{valid: Array<object>, errors: string[]}} Ops that passed and why the others failed.
 */
export const validateEditOps = (operations, blocks, { fieldIds = [] } = {}) => {
  const byId = new Map((blocks || []).map((block) => [block.id, block]));
  const validIdsHint = () => {
    const ids = (blocks || []).map((block) => block.id);
    if (ids.length <= 12) return ids.join(", ");
    return `${ids.slice(0, 6).join(", ")} … ${ids.slice(-3).join(", ")}`;
  };
  const docText = (blocks || []).map((block) => block.text || "").join("\n");
  const valid = [];
  const errors = [];

  (operations || []).forEach((raw, index) => {
    const op = raw && typeof raw === "object" ? raw : {};
    const where = `Operation ${index + 1} (${op.op || "missing op"})`;
    if (!EDIT_OPS.includes(op.op)) {
      errors.push(`${where}: unknown op. Use one of ${EDIT_OPS.join(", ")}.`);
      return;
    }
    if (op.op === "fill_field") {
      if (!op.fieldId || !op.html) {
        errors.push(`${where}: fill_field needs fieldId and html.`);
        return;
      }
      if (fieldIds.length && !fieldIds.includes(op.fieldId)) {
        errors.push(`${where}: unknown field ${op.fieldId}. Valid fields: ${fieldIds.join(", ")}.`);
        return;
      }
      valid.push({ op: op.op, fieldId: op.fieldId, html: op.html });
      return;
    }
    if (op.op === "replace_text") {
      if (!op.find) {
        errors.push(`${where}: replace_text needs "find" (exact text) and "replace".`);
        return;
      }
      if (op.blockId) {
        const block = byId.get(op.blockId);
        if (!block) {
          errors.push(`${where}: unknown block id ${op.blockId}. Valid ids: ${validIdsHint()}.`);
          return;
        }
        if (block.locked) {
          errors.push(`${where}: block ${op.blockId} is locked and must not change.`);
          return;
        }
        const count = countOccurrences(block.text || "", op.find);
        if (count === 0) {
          errors.push(`${where}: "${op.find.slice(0, 60)}" does not appear in block ${op.blockId}. Quote its text exactly.`);
          return;
        }
        if (count > 1) {
          errors.push(`${where}: "${op.find.slice(0, 60)}" appears ${count} times in block ${op.blockId}; quote more surrounding text.`);
          return;
        }
      } else {
        const count = countOccurrences(docText, op.find);
        if (count !== 1) {
          errors.push(
            count === 0
              ? `${where}: "${op.find.slice(0, 60)}" does not appear in the document. Quote its text exactly or add a blockId.`
              : `${where}: "${op.find.slice(0, 60)}" appears ${count} times; add a blockId or quote more text.`
          );
          return;
        }
      }
      valid.push({ op: op.op, ...(op.blockId ? { blockId: op.blockId } : {}), find: op.find, replace: op.replace ?? "" });
      return;
    }
    const block = byId.get(op.blockId);
    if (!block) {
      errors.push(`${where}: unknown block id ${op.blockId || "(none)"}. Valid ids: ${validIdsHint()}.`);
      return;
    }
    if (block.locked && op.op !== "insert_after" && op.op !== "insert_before") {
      errors.push(`${where}: block ${op.blockId} is locked and must not change.`);
      return;
    }
    if (op.op !== "delete_block" && !String(op.html || "").trim()) {
      errors.push(`${where}: ${op.op} needs html.`);
      return;
    }
    if (op.op === "replace_block" && block.type === "pageBreak" && !htmlHasPageBreak(op.html)) {
      // Rewriting a page break into text would silently drop it; adding content goes next to it.
      errors.push(`${where}: block ${op.blockId} is a page break. Add content with insert_before / insert_after, or delete_block only when the user asked to remove the page break.`);
      return;
    }
    valid.push({ op: op.op, blockId: op.blockId, ...(op.op !== "delete_block" ? { html: op.html } : {}) });
  });

  return { valid, errors };
};

/**
 * Human label for the step that edits specific blocks, e.g. `Editing "Scope"`.
 * @param {Array<object>} blocks - Editor blocks.
 * @param {string[]} targetIds - Targeted block ids.
 * @returns {string|null} Label, or null when nothing was targeted.
 */
export const describeTargets = (blocks, targetIds) => {
  if (!targetIds?.length) return null;
  const list = blocks || [];
  const first = list.find((block) => block.id === targetIds[0]);
  if (!first) return null;
  // Name the section: the target itself when it is a heading, else the nearest heading above it.
  const index = list.indexOf(first);
  let heading = first.type === "heading" ? first : null;
  for (let i = index - 1; !heading && i >= 0; i -= 1) {
    if (list[i].type === "heading") heading = list[i];
  }
  const name = String((heading || first).text || "").replace(/\s+/g, " ").trim().slice(0, 48);
  if (!name) return null;
  return `Editing "${name}"`;
};
